import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../lib/session';
import { useResourceMapped } from '../lib/api';
import { mapArt14, type ApiArt14Row } from '../lib/adapt';
import type { Art14Row } from '../lib/contract';
import { CreateOrg } from './CreateOrg';

const LOGO = (
  <svg width="26" height="30" viewBox="0 0 64 72" fill="none" aria-hidden="true">
    <path d="M 10,8 L 54,8 L 54,52 L 32,66 L 10,52 Z" fill="none" stroke="var(--teal)" strokeWidth="8" strokeLinejoin="miter" strokeLinecap="square" />
  </svg>
);

const NAV = [
  { to: '/app/today', ico: '◧', label: 'Today' },
  { to: '/app/products', ico: '▣', label: 'Products' },
  { to: '/app/knowledge-base', ico: '◎', label: 'Knowledge Base' },
  { to: '/app/incidents', ico: '◷', label: 'Incidents' },
];

export function AppShell() {
  const { status, session, orgs, switchOrg, signOut } = useSession();
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('bp.sidebar') === 'collapsed');
  useEffect(() => { localStorage.setItem('bp.sidebar', collapsed ? 'collapsed' : ''); }, [collapsed]);
  // Theme persists like the sidebar pref (was lost on every reload — user report 2026-07-20).
  const [theme, setTheme] = useState<string | null>(() => localStorage.getItem('bp.theme'));
  useEffect(() => {
    if (theme) { document.documentElement.setAttribute('data-theme', theme); localStorage.setItem('bp.theme', theme); }
  }, [theme]);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setDrawer(false); }, [loc.pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawer(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  function toggleTheme() {
    const cur = theme ?? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    setTheme(cur === 'dark' ? 'light' : 'dark'); // the effect applies + persists it
  }

  // Live badge: number of OPEN Art.14 incidents (milestone not complete).
  const art14 = useResourceMapped<ApiArt14Row[], Art14Row[]>(
    session, status === 'ready' ? '/api/art14' : null, (rows) => rows.map(mapArt14),
  );
  const openIncidents = (art14.data ?? []).filter((a) => a.milestone !== 'complete').length;

  // ── Auth gate (P1): restore → sign-in → create-org → app ──────────────────
  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--page-bg)', color: 'var(--muted)' }}>
        <span className="live"><span className="dot" /> restoring session…</span>
      </div>
    );
  }
  if (status === 'signedOut') return <Navigate to="/signin" replace />;
  if (status === 'needsOrg') return <CreateOrg />;

  const initials = session.userName.split(/\s+/).map((w) => w[0]).slice(0, 2).join('');

  return (
    <div id="app" className={`${drawer ? 'drawer-open' : ''}${collapsed ? ' collapsed' : ''}`} style={{ display: 'grid' }}>
      <aside className="sidebar">
        <div className="brand" onClick={() => navigate('/app/today')} style={{ cursor: 'pointer' }}>
          {LOGO}
          <div className="word"><b>BicPort</b><span>Report</span></div>
        </div>
        <nav>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
              <span className="ico">{n.ico}</span><span>{n.label}</span>
              {n.label === 'Incidents' && openIncidents > 0 && <span className="count">{openIncidents}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="nav-foot">
          <NavLink to="/app/settings" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            <span className="ico">⚙</span><span>Settings</span>
          </NavLink>
          <button id="collapseBtn" className="nav-item" onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer' }}>
            <span className="ico">⟨</span><span>Collapse</span>
          </button>
        </div>
      </aside>
      <div className="scrim" onClick={() => setDrawer(false)} />

      <div className="main">
        <header className="topbar">
          <div className="topleft">
            <button className="hamburger" aria-label="Menu" onClick={() => setDrawer((d) => !d)}><span /><span /><span /></button>
            {orgs.length > 1 ? (
              <select
                className="select" value={session.organizationId}
                onChange={(e) => void switchOrg(e.target.value)}
                style={{ width: 'auto', padding: '6px 10px', fontSize: 'calc(13px*var(--fs))', fontWeight: 700 }}
                title="Switch organization"
              >
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            ) : (
              <div className="org"><b>{session.orgName}</b><span className="live"><span className="dot" /> live</span></div>
            )}
          </div>
          <div className="top-right">
            <button className="theme-toggle" onClick={toggleTheme} title="Toggle theme" aria-label="Toggle theme">◐</button>
            <div className="avatar">{initials}</div>
            <div className="who"><div className="n">{session.userName}</div><div className="r">{session.role === 'manager' ? 'OEM Manager' : 'OEM User'}</div></div>
            <button className="btn btn-ghost sm" onClick={() => { void signOut().then(() => window.location.assign('/')); }}>Sign out</button>
          </div>
        </header>
        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
